// Collaborations.jsx — v9
// · Fixed: all CollabCards are exactly the same height (360px) regardless of content
// · Added: tracker delete support
// · v8: Duplicate dedupe — outbound wins over inbound creator_deal duplicates;
//   outbound card links to the internal incoming deal.
// · NEW (v9): Visual polish —
//   - Hero shortened 180 → 150px so the content area has real breathing room
//   - Title gets a reserved 2-line block (consistent alignment across cards) and
//     ellipsizes cleanly instead of clipping into the card; full name on hover
//   - "Internal deal" indicator moved out of the content area onto the hero as a
//     clickable overlay badge, so it never competes with title/details for space
//   - Detail rows get a soft bottom fade instead of a hard cut when they overflow
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  Grid, Card, CardContent, CardMedia, Checkbox, IconButton, Button,
  TextField, TableContainer, Table, TableHead, TableRow, Tabs, Tab,
  TableBody, Pagination, Chip, Avatar, Stack, InputAdornment,
  Tooltip, ButtonGroup, TableCell, Badge, Skeleton,
  Divider, useMediaQuery, List, ListItemButton, ListItemAvatar,
  ListItemText, ToggleButtonGroup, ToggleButton, LinearProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Delete as DeleteIcon, ContentCopy as ContentCopyIcon, Share as ShareIcon,
  Search as SearchIcon, GridView as GridViewIcon, TableRows as TableRowsIcon,
  AttachMoney as AttachMoneyIcon,
  Archive as ArchiveIcon, Add as AddIcon, Link as LinkIcon,
  TrackChanges as TrackerIcon, PeopleOutline as PeopleIcon,
  Campaign as CampaignIcon, Handshake as HandshakeIcon,
  OpenInNew as OpenInNewIcon, CheckCircleOutline as ActiveIcon,
  HourglassEmpty as PendingIconMui, Inventory2 as ArchiveIconOutlined,
  AccountBalanceWallet as WalletIcon,
  PersonAdd as PersonAddIcon, ArrowForwardIos as ArrowIcon,
  Refresh as RefreshIcon, Payments as PayoutIcon,
  ChevronRight as ChevronRightIcon, ChevronLeft as ChevronLeftIcon,
  Inbox as InboxIcon, Send as SendIcon, AllInbox as AllIcon,
  GridView as RosterIcon, Person as PersonIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery } from 'react-query';

import client from '../../../API';
import API from '../../../API';
import profilePhoto from '../../../Components/globalAssets/ppfLogo.png';
import useAuth from '../../../Hooks/use-auth';
import useAlertDialog from '../../../Components/useAlertDialog';
import AlertDialog from '../../../Components/AlertDialog';
import useIsDesktop from '../../../Hooks/useIsDesktop';
import { NewTracker } from '../../Trackers/newTracker';
import { Tracker } from '../../Trackers/tracker';
import CollabsBalanceLedger from './newAgency/Collabsbalance';

/* ================================================================== */
/* Cookie utils                                                        */
/* ================================================================== */
const COLLAB_COOKIE_KEYS = {
  ACCOUNT_STATUS: 'blitz_account_status',
  COLLABS_ACCESS: 'blitz_collabs_access',
};
const cookieUtils = {
  set: (key, value, days = 30) => {
    const e = new Date();
    e.setTime(e.getTime() + days * 86400000);
    document.cookie = `${key}=${encodeURIComponent(value)};expires=${e.toUTCString()};path=/;SameSite=Lax`;
  },
  get: (key) => {
    const n = key + '=';
    for (let c of decodeURIComponent(document.cookie).split(';')) {
      c = c.trim();
      if (c.indexOf(n) === 0) return c.substring(n.length);
    }
    return null;
  },
};

function useCollabCompanyData() {
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const [accountStatus, setAccountStatus] = useState(
    () => cookieUtils.get(COLLAB_COOKIE_KEYS.ACCOUNT_STATUS) || ''
  );
  const [collabsAccess, setCollabsAccess] = useState(
    () => cookieUtils.get(COLLAB_COOKIE_KEYS.COLLABS_ACCESS) === 'true'
  );
  useQuery(
    ['company', userInfo?.company_id],
    () => client.companies.listFetch({ id: userInfo?.company_id }),
    {
      enabled: !!userInfo?.company_id,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 30,
      onSuccess: (data) => {
        const company = (Array.isArray(data) ? data[0] : null) || data?.data || data || {};
        const newStatus = company.account_status || '';
        const newAccess = !!company.collabs_access;
        setAccountStatus(newStatus);
        setCollabsAccess(newAccess);
        if (newStatus) cookieUtils.set(COLLAB_COOKIE_KEYS.ACCOUNT_STATUS, newStatus);
        cookieUtils.set(COLLAB_COOKIE_KEYS.COLLABS_ACCESS, String(newAccess));
      },
    }
  );
  return {
    isAgency: accountStatus === 'Agency' || accountStatus === 'AlphaFree',
    hasCollabsAccess: collabsAccess,
  };
}

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */
const fmtSum = (sum) => {
  const n = typeof sum === 'number' ? sum : parseFloat(String(sum).replace(/[^\d.]/g, ''));
  if (isNaN(n)) return '$0.00';
  return `$${n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};
const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const STATUS_COLOR = {
  Active: 'success', active: 'success', ACTIVE: 'success', Launched: 'success',
  Pending: 'warning', pending: 'warning', Draft: 'warning', Pitched: 'warning',
  Completed: 'info', COMPLETED: 'info', completed: 'info',
  paid: 'success', Archived: 'default', DELETED: 'error',
};

const TYPE_META = {
  campaign:     { label: 'Campaign',     color: 'secondary' },
  partnership:  { label: 'Partnership',  color: 'primary' },
  casting_call: { label: 'Casting',      color: 'warning' },
  tracker:      { label: 'Tracker',      color: 'info' },
  creator_deal: { label: 'Incoming',     color: 'success' },
};

function getSortDate(item) {
  if (item.type === 'casting_call' || item.type === 'tracker')
    return item.date_created ? new Date(item.date_created) : new Date(0);
  const p = new Date(item.proposal_date);
  const f = item.finish_date ? new Date(item.finish_date) : null;
  if (!isNaN(p)) return p;
  if (f && !isNaN(f)) return f;
  return new Date(0);
}

function isActiveLive(item) {
  if (item.type === 'campaign') return ['Launched', 'active'].includes(item.campaign_status);
  if (item.type === 'casting_call') return item.status === 'ACTIVE';
  if (item.type === 'tracker') return item.status?.toLowerCase() === 'active';
  if (item.type === 'creator_deal') return ['active', 'Active', 'Launched'].includes(item.status);
  return item.status === 'active';
}

function getCategory(item) {
  if (isActiveLive(item)) return 0;
  if (item.unread_notifs > 0) return 1;
  const s = (item.type === 'campaign' ? item.campaign_status : item.status)?.toLowerCase();
  if (['draft', 'pending', 'pitched'].includes(s)) return 2;
  if (['completed'].includes(s)) return 3;
  return 4;
}

/* ================================================================== */
/* Summary Card                                                        */
/* ================================================================== */
const SummaryCard = ({ icon, label, value, sublabel, color = 'primary', onClick }) => {
  const theme = useTheme();
  return (
    <Card elevation={0} onClick={onClick} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper', p: 2.5, cursor: onClick ? 'pointer' : 'default', transition: 'all 0.2s ease', '&:hover': onClick ? { borderColor: theme.palette[color].main, boxShadow: `0 4px 16px ${alpha(theme.palette[color].main, 0.18)}`, transform: 'translateY(-2px)' } : {} }}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: alpha(theme.palette[color].main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {React.cloneElement(icon, { sx: { color: `${color}.main`, fontSize: 23 } })}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2, mb: 0.25 }}>{value}</Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>{label}</Typography>
          {sublabel && <Typography variant="caption" color="text.disabled">{sublabel}</Typography>}
        </Box>
      </Stack>
    </Card>
  );
};

/* ================================================================== */
/* CollabCard — uniform 360px height across all card types            */
/* v9: hero 150px, reserved 2-line title block, overlay internal-deal */
/* badge, soft fade on overflowing details                            */
/* ================================================================== */
const CARD_HEIGHT = 360;
const IMAGE_HEIGHT = 150;
// Title: 2 lines @ 0.95rem * 1.35 line-height ≈ 41px. Reserving the block
// keeps every card's details starting at the same y — no clipping, no jumping.
const TITLE_BLOCK_HEIGHT = 42;

const CollabCard = ({ item, isSelected, onToggleSelect, onOpen, onDelete, onShare, onCopy }) => {
  const theme = useTheme();
  const isCampaign    = item.type === 'campaign';
  const isPartnership = item.type === 'partnership';
  const isTracker     = item.type === 'tracker';
  const isCasting     = item.type === 'casting_call';
  const isIncoming    = item.type === 'creator_deal';
  const status        = isCampaign ? item.campaign_status : item.status;
  const statusColor   = STATUS_COLOR[status] || 'default';
  const meta          = TYPE_META[item.type] || { label: item.type, color: 'default' };
  const itemId        = `${item.type}-${item.id}`;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
        overflow: 'hidden',
        transition: 'all 0.22s ease',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        height: CARD_HEIGHT,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 3,
          zIndex: 2,
          background: isIncoming
            ? `linear-gradient(90deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.4)})`
            : isCampaign
            ? `linear-gradient(90deg, ${theme.palette.secondary.main}, ${alpha(theme.palette.secondary.main, 0.4)})`
            : isTracker
            ? `linear-gradient(90deg, ${theme.palette.info.main}, ${alpha(theme.palette.info.main, 0.4)})`
            : isCasting
            ? `linear-gradient(90deg, ${theme.palette.warning.main}, ${alpha(theme.palette.warning.main, 0.4)})`
            : `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.4)})`,
        },
        '&:hover': {
          borderColor: isIncoming ? theme.palette.success.main : theme.palette.primary.main,
          boxShadow: `0 8px 28px ${alpha(isIncoming ? theme.palette.success.main : theme.palette.primary.main, 0.13)}`,
          transform: 'translateY(-4px)',
        },
      }}
    >
      {/* ── Image / hero area — fixed height ── */}
      <Box
        sx={{ position: 'relative', cursor: 'pointer', flexShrink: 0, height: IMAGE_HEIGHT }}
        onClick={() => onOpen(item)}
      >
        {isTracker ? (
          <Box sx={{ height: IMAGE_HEIGHT, background: `linear-gradient(135deg, ${theme.palette.info.dark} 0%, ${theme.palette.info.main} 100%)`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <TrackerIcon sx={{ fontSize: 44, color: 'white', opacity: 0.85 }} />
            <Typography variant="caption" sx={{ color: 'white', opacity: 0.7, fontWeight: 700, letterSpacing: 1 }}>TRACKER</Typography>
          </Box>
        ) : isIncoming ? (
          item.pfphref && item.pfphref !== profilePhoto ? (
            <CardMedia component="img" height={IMAGE_HEIGHT} image={item.pfphref} alt={item.name} sx={{ objectFit: 'cover' }} />
          ) : (
            <Box sx={{ height: IMAGE_HEIGHT, background: `linear-gradient(135deg, ${alpha(theme.palette.success.dark, 0.75)} 0%, ${alpha(theme.palette.success.main, 0.55)} 100%)`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Avatar src={item.creator_pfphref} sx={{ width: 50, height: 50, border: '2px solid rgba(255,255,255,0.45)', boxShadow: '0 2px 12px rgba(0,0,0,0.18)' }}>{(item.creator_name || '?')[0]}</Avatar>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600, letterSpacing: 0.5 }}>{item.creator_name || 'Creator Deal'}</Typography>
            </Box>
          )
        ) : (
          <CardMedia component="img" height={IMAGE_HEIGHT} image={item.pfphref || profilePhoto} alt={item.name} sx={{ objectFit: 'cover' }} />
        )}
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '55%', background: `linear-gradient(transparent, ${alpha(theme.palette.common.black, 0.72)})`, pointerEvents: 'none' }} />

        {/* ── v9: internal duplicate flag lives on the hero now — clickable badge,
            top-left, completely out of the content area ── */}
        {item._internalDealLink && (
          <Tooltip title="This deal was sent to your own roster — view the internal incoming deal">
            <Chip
              icon={<InboxIcon sx={{ fontSize: 13 }} />}
              label="Internal deal"
              size="small"
              onClick={(e) => { e.stopPropagation(); onOpen({ type: 'creator_deal', _link: item._internalDealLink }); }}
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                height: 22,
                fontSize: '0.62rem',
                fontWeight: 700,
                cursor: 'pointer',
                color: '#fff',
                bgcolor: alpha(theme.palette.info.dark, 0.85),
                backdropFilter: 'blur(4px)',
                border: `1px solid ${alpha('#fff', 0.25)}`,
                '&:hover': { bgcolor: theme.palette.info.main },
                '& .MuiChip-icon': { color: '#fff' },
              }}
            />
          </Tooltip>
        )}

        {item.unread_notifs > 0 && (
          <Chip label={`${item.unread_notifs} new`} color="error" size="small" sx={{ position: 'absolute', top: 10, right: 10, fontWeight: 700, pointerEvents: 'none', height: 22, fontSize: '0.65rem' }} />
        )}
        <Box sx={{ position: 'absolute', bottom: 10, left: 10, pointerEvents: 'none' }}>
          <Chip label={meta.label} color={meta.color} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', height: 22 }} />
        </Box>
        <Box sx={{ position: 'absolute', bottom: 10, right: 10, pointerEvents: 'none' }}>
          <Chip label={String(status || 'N/A').charAt(0).toUpperCase() + String(status || 'N/A').slice(1)} color={statusColor} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', height: 22 }} />
        </Box>
      </Box>

      {/* ── Content area — reserved title block + fading details ── */}
      <CardContent
        sx={{
          flex: 1,
          pt: 1.75,
          pb: 1.25,
          px: 2.25,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          overflow: 'hidden',
          minHeight: 0,
        }}
        onClick={() => onOpen(item)}
      >
        {/* Title always gets exactly 2 lines of space — long names ellipsize
            cleanly with a hover tooltip, short names stay vertically aligned */}
        <Tooltip title={item.name || ''} placement="top" enterDelay={500}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              height: TITLE_BLOCK_HEIGHT,
              flexShrink: 0,
              lineHeight: 1.35,
              fontSize: '0.95rem',
              letterSpacing: '-0.01em',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            }}
          >
            {item.name}
          </Typography>
        </Tooltip>

        {/* Details — soft fade at the bottom if there's more than fits */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0, left: 0, right: 0,
              height: 14,
              background: `linear-gradient(transparent, ${isSelected ? alpha(theme.palette.primary.main, 0.04) : theme.palette.background.paper})`,
              pointerEvents: 'none',
            },
          }}
        >
          <Stack spacing={0.75}>
            {isCampaign && (<>
              {item.campaign_sum && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <AttachMoneyIcon sx={{ fontSize: 15, color: 'success.main' }} />
                  <Typography variant="body2" fontWeight={700} color="success.main">{fmtSum(item.campaign_sum)}</Typography>
                </Stack>
              )}
              {item.user?.username && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <PersonIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary" noWrap>{item.user.username}</Typography>
                </Stack>
              )}
              {item.creators?.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <PeopleIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary">{item.creators.length} creator{item.creators.length !== 1 ? 's' : ''}</Typography>
                </Stack>
              )}
            </>)}

            {isPartnership && (<>
              {item.total_sum > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <AttachMoneyIcon sx={{ fontSize: 15, color: 'success.main' }} />
                  <Typography variant="body2" fontWeight={700} color="success.main">{fmtSum(item.total_sum)}</Typography>
                </Stack>
              )}
              {(item.email || item.creator) && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <PersonIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary" noWrap>{item.email || item.creator}</Typography>
                </Stack>
              )}
            </>)}

            {isTracker && (<>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <LinkIcon sx={{ fontSize: 14, color: 'info.main' }} />
                <Typography variant="body2" fontWeight={600} color="info.main">{item.links?.length || 0} tracked link{item.links?.length !== 1 ? 's' : ''}</Typography>
              </Stack>
              {item.date_created && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <CalendarIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.secondary">Created {new Date(item.date_created).toLocaleDateString()}</Typography>
                </Stack>
              )}
            </>)}

            {isCasting && (<>
              {item.budget > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <AttachMoneyIcon sx={{ fontSize: 15, color: 'success.main' }} />
                  <Typography variant="body2" fontWeight={700} color="success.main">${parseFloat(item.budget).toFixed(2)}</Typography>
                </Stack>
              )}
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <PersonIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">{item.user_role === 'owner' ? 'Managing' : 'Collaborating'}</Typography>
              </Stack>
            </>)}

            {isIncoming && (<>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar src={item.creator_pfphref} sx={{ width: 20, height: 20, fontSize: '0.6rem' }}>{(item.creator_name || '?')[0]}</Avatar>
                <Typography variant="caption" color="text.secondary" noWrap fontWeight={600}>{item.creator_name}</Typography>
              </Stack>
              {item.amount > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <AttachMoneyIcon sx={{ fontSize: 15, color: 'success.main' }} />
                  <Typography variant="body2" fontWeight={700} color="success.main">{fmtMoney(item.amount)}</Typography>
                </Stack>
              )}
            </>)}
          </Stack>
        </Box>
      </CardContent>

      {/* ── Action bar — always pinned to bottom ── */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: alpha(theme.palette.background.default, 0.55),
          flexShrink: 0,
        }}
      >
        <Checkbox checked={isSelected} size="small" onChange={() => onToggleSelect(itemId)} sx={{ p: 0.5 }} />
        <Stack direction="row" spacing={0.5}>
          {isCampaign && (<>
            <Tooltip title="Share link">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onShare(item.id); }} sx={{ width: 30, height: 30, bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main' }}>
                <ShareIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onCopy(item.id); }} sx={{ width: 30, height: 30, bgcolor: alpha(theme.palette.secondary.main, 0.1), color: 'secondary.main' }}>
                <ContentCopyIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </>)}
          {/* ── DELETE: campaigns, partnerships, AND trackers ── */}
          {(isCampaign || isPartnership || isTracker) && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(item); }} sx={{ width: 30, height: 30, bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main' }}>
                <DeleteIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Open">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpen(item); }} sx={{ width: 30, height: 30, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
              <OpenInNewIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Card>
  );
};

/* ================================================================== */
/* AgencyCreatorSidebar                                               */
/* ================================================================== */
const SIDEBAR_FULL = 300;
const SIDEBAR_MINI = 52;

const AgencyCreatorSidebar = ({ minimized, onToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const [dealTab, setDealTab] = useState('campaigns');
  const [search, setSearch] = useState('');

  const { data: creators, isLoading, refetch } = useQuery({
    queryKey: ['agency', 'creators'],
    queryFn: () => API.creators.list({ only_agency: true }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: creatorFull, isLoading: dealLoading } = useQuery(
    ['agency', 'creator', selectedId],
    () => API.agency.getCreatorById(selectedId),
    { enabled: !!selectedId, refetchOnWindowFocus: false, staleTime: 1000 * 60 * 5 }
  );

  const filtered = useMemo(() => {
    if (!creators) return [];
    if (!search) return creators;
    const s = search.toLowerCase();
    return creators.filter((c) => (c.creator || '').toLowerCase().includes(s) || (c.display_name || '').toLowerCase().includes(s));
  }, [creators, search]);

  const deals = useMemo(() => {
    if (!creatorFull) return { campaigns: [], partnerships: [], payouts: [] };
    return {
      campaigns: (creatorFull.campaigns || []).filter((c) => !['Archived', 'deleted'].includes(c.campaign_status)).map((c) => ({ id: c.id, type: 'campaign', name: c.name || `Campaign #${c.id}`, status: c.campaign_status || 'pending', amount: parseFloat(c.price || c.campaign_sum || 0), link: `/agency/creators/${selectedId}/collaborations/campaigns/${c.id}` })),
      partnerships: (creatorFull.partnerships || []).filter((p) => p.status !== 'DELETED').map((p) => ({ id: p.id, type: 'partnership', name: p.name || `Partnership #${p.id}`, status: p.status || 'pending', amount: parseFloat(p.total_sum || 0), link: `/agency/creators/${selectedId}/collaborations/partnerships/${p.id}` })),
      payouts: (creatorFull.payouts || []).map((p) => ({ id: p.id, type: 'payout', name: p.po_number || `Payout #${p.id}`, status: p.status || 'pending', amount: parseFloat(p.amount || 0), link: `/agency/creators/${selectedId}/payouts` })),
    };
  }, [creatorFull, selectedId]);

  const ICON_MAP = { campaign: CampaignIcon, partnership: HandshakeIcon, payout: PayoutIcon };
  const COLOR_MAP = { campaign: theme.palette.secondary.main, partnership: theme.palette.success.main, payout: theme.palette.info.main };

  if (minimized) {
    return (
      <Box sx={{ width: SIDEBAR_MINI, height: '100%', bgcolor: 'background.paper', borderLeft: `1px solid ${theme.palette.divider}`, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2, gap: 1.5 }}>
        <Tooltip title="Expand creator panel" placement="left"><IconButton size="small" onClick={onToggle} sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.08) } }}><ChevronLeftIcon /></IconButton></Tooltip>
        <Tooltip title="Creator Roster" placement="left"><IconButton size="small" onClick={onToggle} sx={{ color: 'success.main' }}><PeopleIcon sx={{ fontSize: 22 }} /></IconButton></Tooltip>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="All Creators" placement="left"><IconButton size="small" onClick={() => navigate('/agency/creators')} sx={{ color: 'text.secondary', mb: 1 }}><ArrowIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ width: SIDEBAR_FULL, height: '100%', bgcolor: 'background.paper', borderLeft: `1px solid ${theme.palette.divider}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.75, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.success.main, 0.04), flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: alpha(theme.palette.success.main, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}><PeopleIcon sx={{ fontSize: 17, color: 'success.main' }} /></Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.2 }}>Creator Roster</Typography>
              <Typography variant="caption" color="text.secondary">{filtered.length} creator{filtered.length !== 1 ? 's' : ''}</Typography>
            </Box>
            <Chip label="AGENCY" size="small" color="success" sx={{ height: 16, fontSize: '0.52rem', fontWeight: 700 }} />
          </Stack>
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Refresh"><IconButton size="small" onClick={() => refetch()}><RefreshIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
            <Tooltip title="Collapse"><IconButton size="small" onClick={onToggle}><ChevronRightIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
          </Stack>
        </Stack>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search creators..." style={{ width: '100%', padding: '6px 10px', border: `1px solid ${alpha(theme.palette.divider, 0.55)}`, borderRadius: 8, fontSize: 12.5, outline: 'none', backgroundColor: alpha(theme.palette.background.default, 0.75), color: theme.palette.text.primary }} />
      </Box>

      {selectedId ? (
        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`, flexShrink: 0 }}>
            <IconButton size="small" onClick={() => setSelectedId(null)} sx={{ p: 0.25 }}><ChevronLeftIcon sx={{ fontSize: 17 }} /></IconButton>
            <Avatar src={filtered.find((c) => c.creator === selectedId)?.pfphref} sx={{ width: 30, height: 30 }}>{(selectedId[0] || '?').toUpperCase()}</Avatar>
            <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1 }}>{filtered.find((c) => c.creator === selectedId)?.display_name || selectedId}</Typography>
            <Tooltip title="Full profile"><IconButton size="small" onClick={() => navigate(`/agency/creators/${selectedId}`)}><OpenInNewIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
          </Box>
          <Stack direction="row" spacing={0.25} sx={{ px: 1.5, py: 1, flexShrink: 0, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}` }}>
            {['campaigns', 'partnerships', 'payouts'].map((k) => (
              <Button key={k} size="small" variant={dealTab === k ? 'contained' : 'text'} onClick={() => setDealTab(k)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.68rem', px: 1.25, py: 0.35, minWidth: 0, borderRadius: 1.5 }}>
                {k.charAt(0).toUpperCase() + k.slice(1)}
                {deals[k]?.length > 0 && <Chip label={deals[k].length} size="small" sx={{ ml: 0.5, height: 15, fontSize: '0.55rem', fontWeight: 700, bgcolor: dealTab === k ? 'rgba(255,255,255,0.28)' : alpha(theme.palette.primary.main, 0.1), color: dealTab === k ? 'white' : 'primary.main' }} />}
              </Button>
            ))}
          </Stack>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
            {dealLoading ? [1,2,3].map((i) => <Skeleton key={i} variant="rounded" height={56} sx={{ mb: 0.75 }} />) : (deals[dealTab] || []).length === 0 ? (
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', pt: 4 }}>No {dealTab}</Typography>
            ) : (deals[dealTab] || []).map((deal) => {
              const DIcon = ICON_MAP[deal.type] || HandshakeIcon;
              const dcolor = COLOR_MAP[deal.type] || theme.palette.primary.main;
              return (
                <Box key={`${deal.type}-${deal.id}`} onClick={() => deal.link && navigate(deal.link)} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, mb: 0.75, borderRadius: 2, border: `1px solid ${alpha(theme.palette.divider, 0.12)}`, bgcolor: alpha(theme.palette.background.paper, 0.65), cursor: deal.link ? 'pointer' : 'default', transition: 'all 0.12s', '&:hover': deal.link ? { bgcolor: alpha(dcolor, 0.06), borderColor: alpha(dcolor, 0.3), transform: 'translateX(2px)' } : {} }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: 1.5, flexShrink: 0, bgcolor: alpha(dcolor, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}><DIcon sx={{ fontSize: 16, color: dcolor }} /></Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={600} noWrap display="block">{deal.name}</Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip label={String(deal.status).charAt(0).toUpperCase() + String(deal.status).slice(1)} size="small" color={STATUS_COLOR[deal.status] || 'default'} sx={{ height: 15, fontSize: '0.55rem', fontWeight: 700 }} />
                      {deal.amount > 0 && <Typography variant="caption" color="success.main" fontWeight={700} sx={{ fontSize: '0.65rem' }}>{fmtMoney(deal.amount)}</Typography>}
                    </Stack>
                  </Box>
                  {deal.link && <ArrowIcon sx={{ fontSize: 12, color: 'action.active', flexShrink: 0 }} />}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto', px: 1, py: 1 }}>
          {isLoading ? [1,2,3,4].map((i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 0.75 }}>
              <Skeleton variant="circular" width={36} height={36} />
              <Box sx={{ flex: 1 }}><Skeleton variant="text" /><Skeleton variant="text" width="60%" /></Box>
            </Box>
          )) : filtered.length === 0 ? (
            <Box sx={{ textAlign: 'center', pt: 5 }}>
              <PeopleIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography variant="caption" color="text.disabled" display="block">No creators yet</Typography>
              <Button size="small" startIcon={<PersonAddIcon />} onClick={() => navigate('/agency/onboard?type=creator')} sx={{ mt: 1, textTransform: 'none', fontSize: '0.75rem' }}>Add one</Button>
            </Box>
          ) : filtered.map((c) => (
            <ListItemButton key={c.creator} onClick={() => setSelectedId(c.creator)} sx={{ borderRadius: 2, mb: 0.5, py: 1, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) } }}>
              <ListItemAvatar sx={{ minWidth: 44 }}><Avatar src={c.pfphref} sx={{ width: 36, height: 36 }}>{(c.display_name || c.creator || '?')[0].toUpperCase()}</Avatar></ListItemAvatar>
              <ListItemText primary={<Typography variant="body2" fontWeight={600} noWrap>{c.display_name || c.creator}</Typography>} secondary={<Typography variant="caption" color="text.secondary" noWrap>@{c.creator}</Typography>} />
              <ArrowIcon sx={{ fontSize: 12, color: 'action.disabled' }} />
            </ListItemButton>
          ))}
        </Box>
      )}

      <Box sx={{ px: 1.75, py: 1.5, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
        <Stack spacing={0.75}>
          <Button fullWidth size="small" variant="outlined" startIcon={<PeopleIcon />} onClick={() => navigate('/agency/creators')} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, fontSize: '0.75rem' }}>All Creators</Button>
          <Button fullWidth size="small" variant="outlined" color="secondary" startIcon={<RosterIcon />} onClick={() => navigate('/agency/roster-settings')} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, fontSize: '0.75rem' }}>Create Roster</Button>
        </Stack>
      </Box>
    </Box>
  );
};

/* ================================================================== */
/* Main Component                                                      */
/* ================================================================== */
const Collaborations = () => {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const { isAgency, hasCollabsAccess } = useCollabCompanyData();

  const [searchQuery, setSearchQuery]     = useState('');
  const [tabValue, setTabValue]           = useState('All');
  const [isPrettyView, setIsPrettyView]   = useState(true);
  const [selectedIds, setSelectedIds]     = useState([]);
  const [searchStatus, setSearchStatus]   = useState('');
  const [page, setPage]                   = useState(0);
  const [dealMode, setDealMode]           = useState('all');
  const [sidebarMinimized, setSidebarMinimized] = useState(true);
  const [openNewTracker, setOpenNewTracker]     = useState(false);
  const [openTrackerDetail, setOpenTrackerDetail] = useState(false);
  const [selectedTracker, setSelectedTracker]   = useState(null);
  const [balanceLedgerOpen, setBalanceLedgerOpen] = useState(false);

  const itemsPerPage = isDesktop ? (isAgency && !sidebarMinimized ? 16 : 20) : 12;
  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  const qCfg = { staleTime: 30000, cacheTime: 300000, refetchOnWindowFocus: false, refetchOnMount: false, retry: 1 };

  const [
    { data: campaignsData,    isLoading: isLoadingCampaigns,    refetch: fetchCampaigns    },
    { data: partnershipsData, isLoading: isLoadingPartnerships, refetch: fetchPartnerships },
    { data: castingCallsData, isLoading: isLoadingCastingCalls, refetch: fetchCastingCalls },
    { data: trackersData,     isLoading: isLoadingTrackers,     refetch: fetchTrackers     },
    { data: sharedTrackersData,                                 refetch: fetchSharedTrackers },
  ] = useQueries([
    { queryKey: 'campaigns',             queryFn: client.campaigns.list,                    ...qCfg, select: (d) => Array.isArray(d) ? d : d.campaigns || [] },
    { queryKey: 'partnerships',          queryFn: client.partnerships.list,                 ...qCfg },
    { queryKey: 'myManagedCastingCalls', queryFn: () => client.castingCalls.getMyManaged(), ...qCfg },
    { queryKey: 'trackers',              queryFn: client.trackers.fetch,                    ...qCfg },
    { queryKey: 'sharedTrackers',        queryFn: client.trackers.fetchShared,              ...qCfg },
  ]);

  const { data: agencyCollabList, isLoading: isLoadingAgencyCollabs } = useQuery(
    'agencyAllCollaborations',
    client.agency.getAllCollaborations,
    {
      ...qCfg,
      refetchOnMount: 'always',
      select: (data) => data?.collaborations ?? [],
    }
  );

  const { data: balanceData } = useQuery(
    ['balance', 'my_balance'],
    () => client.balanceLedger.fetchMyBalance(),
    { ...qCfg, enabled: isAgency, staleTime: 1000 * 60 * 5 }
  );

  useEffect(() => { setPage(0); }, [searchQuery, searchStatus, tabValue, dealMode]);

  const allCollaborations = useMemo(() => {
    if (!campaignsData || !partnershipsData || !castingCallsData) return [];

    const linkedTrackerMap = {};
    const standaloneTrackers = [];
    const ownedIds = new Set();

    (trackersData || []).forEach((t) => {
      ownedIds.add(t.id);
      if (t.campaign_id) linkedTrackerMap[t.campaign_id] = t;
      else standaloneTrackers.push({ ...t, type: 'tracker', unread_notifs: 0, pfphref: t.tracker_photo || null, is_shared: false });
    });
    (sharedTrackersData || []).forEach((t) => {
      if (!ownedIds.has(t.id))
        standaloneTrackers.push({ ...t, type: 'tracker', unread_notifs: 0, pfphref: t.tracker_photo || null, is_shared: true });
    });

    const castingCalls = (castingCallsData?.casting_calls || []).filter((cc) => cc.status !== 'DELETED').map((cc) => ({ ...cc, type: 'casting_call', unread_notifs: cc.unread_notifs || 0, pfphref: cc.header_image || profilePhoto, name: cc.brief?.substring(0, 50) + (cc.brief?.length > 50 ? '...' : ''), _direction: 'outbound' }));

    // ── v8: Build incoming deals first so we can detect internal duplicates ──
    const incomingDealsAll = (agencyCollabList || []).map((c) => {
      const isCampaignType = c.original_type === 'campaign' || c.type === 'campaign';
      return {
        id: c.id,
        type: 'creator_deal',
        unread_notifs: 0,
        pfphref: c.photo || c.pfphref || profilePhoto,
        name: c.name || `${isCampaignType ? 'Campaign' : 'Partnership'} — ${c.creator_display_name || ''}`,
        status: isCampaignType ? (c.campaign_status || c.status) : c.status,
        amount: c.rate || c.total_sum || c.price || 0,
        creator_name: c.creator_display_name || c.creator_id || '',
        creator_pfphref: c.pfphref || null,
        _direction: 'inbound',
        _original_type: c.type,
        _isCampaignType: isCampaignType,
        _link: isCampaignType
          ? `/agency/creators/${c.creator_id}/collaborations/campaigns/${c.id}`
          : `/agency/creators/${c.creator_id}/collaborations/partnerships/${c.id}`,
      };
    });

    // ── Dedupe: internal deals (agency → own roster) appear both outbound and inbound.
    // Keep the outbound card, drop the inbound duplicate, and flag the outbound card
    // with a link to the internal incoming deal. Match is scoped within type so
    // campaign and partnership ids can never collide with each other.
    const outboundCampaignIds    = new Set(campaignsData.map((c) => String(c.id)));
    const outboundPartnershipIds = new Set(partnershipsData.filter((p) => p.status !== 'DELETED').map((p) => String(p.id)));

    const internalDealLinks = { campaign: {}, partnership: {} };
    const incomingDeals = incomingDealsAll.filter((d) => {
      const idStr = String(d.id);
      if (d._isCampaignType && outboundCampaignIds.has(idStr)) {
        internalDealLinks.campaign[idStr] = d._link;
        return false;
      }
      if (!d._isCampaignType && outboundPartnershipIds.has(idStr)) {
        internalDealLinks.partnership[idStr] = d._link;
        return false;
      }
      return true;
    });

    const campaigns = campaignsData.map((c) => ({
      ...c,
      type: 'campaign',
      unread_notifs: c.unread_notifs || 0,
      pfphref: c?.campaign_photo || c.creators?.[0]?.pfphref || profilePhoto,
      _tracker: linkedTrackerMap[c.id] || null,
      _direction: 'outbound',
      _internalDealLink: internalDealLinks.campaign[String(c.id)] || null,
    }));
    const partnerships = partnershipsData.filter((p) => p.status !== 'DELETED').map((p) => ({
      ...p,
      type: 'partnership',
      unread_notifs: p.unread_notifs || 0,
      pfphref: p.pfphref || profilePhoto,
      _direction: 'outbound',
      _internalDealLink: internalDealLinks.partnership[String(p.id)] || null,
    }));

    return [...campaigns, ...partnerships, ...castingCalls, ...standaloneTrackers, ...incomingDeals].sort((a, b) => {
      const cA = getCategory(a); const cB = getCategory(b);
      if (cA !== cB) return cA - cB;
      return getSortDate(b) - getSortDate(a);
    });
  }, [campaignsData, partnershipsData, castingCallsData, trackersData, sharedTrackersData, agencyCollabList]);

  const filteredCollaborations = useMemo(() => {
    return allCollaborations.filter((item) => {
      if (dealMode === 'outbound' && item._direction === 'inbound') return false;
      if (dealMode === 'inbound'  && item._direction !== 'inbound') return false;

      if (tabValue !== 'Archived') {
        if (item.type !== 'creator_deal') {
          if ((item.type === 'campaign'     && item.campaign_status === 'Archived')  ||
              (item.type === 'partnership'  && item.status === 'DELETED')            ||
              (item.type === 'casting_call' && item.status === 'DELETED')            ||
              (item.type === 'tracker'      && item.status?.toLowerCase() === 'archived'))
            return false;
        }
      }

      let tabMatch = true;
      if      (tabValue === 'Campaigns')     tabMatch = item.type === 'campaign';
      else if (tabValue === 'Partnerships')  tabMatch = item.type === 'partnership';
      else if (tabValue === 'Casting Calls') tabMatch = item.type === 'casting_call';
      else if (tabValue === 'Trackers')      tabMatch = item.type === 'tracker';
      else if (tabValue === 'Incoming')      tabMatch = item.type === 'creator_deal';
      else if (tabValue === 'Archived') {
        tabMatch =
          (item.type === 'campaign'     && item.campaign_status === 'Archived') ||
          (item.type === 'partnership'  && ['DELETED','Archived'].includes(item.status)) ||
          (item.type === 'casting_call' && ['DELETED','Archived'].includes(item.status)) ||
          (item.type === 'tracker'      && item.status?.toLowerCase() === 'archived');
      }

      let statusMatch = true;
      if (searchStatus === 'active')         statusMatch = isActiveLive(item);
      else if (searchStatus === 'pending')   statusMatch = item.type === 'campaign' ? item.campaign_status === 'Draft' : item.type === 'creator_deal' ? ['pending','Pitched','active','Active','Launched','Accepted'].includes(item.status) : ['pending','Pitched'].includes(item.status);
      else if (searchStatus === 'completed') statusMatch = ['completed','Completed','COMPLETED'].includes(item.type === 'campaign' ? item.campaign_status : item.status);

      const searchMatch =
        (item.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.creator_name || '').toLowerCase().includes(searchQuery.toLowerCase());

      return tabMatch && statusMatch && searchMatch;
    });
  }, [allCollaborations, searchQuery, tabValue, searchStatus, dealMode]);

  const paginatedCollaborations = useMemo(
    () => filteredCollaborations.slice(page * itemsPerPage, (page + 1) * itemsPerPage),
    [filteredCollaborations, page, itemsPerPage]
  );

  const activeCount   = allCollaborations.filter((i) => i._direction !== 'inbound' && isActiveLive(i)).length;
  const withUnread    = allCollaborations.filter((i) => i.unread_notifs > 0).length;
  const draftCount    = allCollaborations.filter((i) => { const s = (i.type === 'campaign' ? i.campaign_status : i.status)?.toLowerCase(); return s === 'draft' || s === 'pending'; }).length;
  const incomingCount = allCollaborations.filter((i) => i._direction === 'inbound').length;
  const balance       = parseFloat(balanceData?.balance || 0);

  const tabCounts = useMemo(() => ({
    All: allCollaborations.filter((i) => {
      if (i.type === 'campaign'     && i.campaign_status === 'Archived') return false;
      if (i.type === 'partnership'  && i.status === 'DELETED')           return false;
      if (i.type === 'casting_call' && i.status === 'DELETED')           return false;
      if (i.type === 'tracker'      && i.status?.toLowerCase() === 'archived') return false;
      return true;
    }).length,
    Campaigns:       allCollaborations.filter((i) => i.type === 'campaign'     && i.campaign_status !== 'Archived').length,
    Partnerships:    allCollaborations.filter((i) => i.type === 'partnership'  && i.status !== 'DELETED').length,
    'Casting Calls': allCollaborations.filter((i) => i.type === 'casting_call' && i.status !== 'DELETED').length,
    Trackers:        allCollaborations.filter((i) => i.type === 'tracker'      && i.status?.toLowerCase() !== 'archived').length,
    Incoming:        incomingCount,
  }), [allCollaborations, incomingCount]);

  const { mutate: deleteCampaign }          = useMutation(client.campaigns.delete,    { onSuccess: () => { openDialog('Success','Campaign deleted.',    closeDialog, closeDialog,'Ok'); fetchCampaigns(); },    onError: (e) => openDialog('Error', e?.response?.data?.error || e.message, closeDialog, closeDialog,'Ok') });
  const { mutate: updatePartnershipStatus } = useMutation(({ id, status }) => client.partnerships.updateStatus(id, { status }), { onSuccess: () => { openDialog('Success','Partnership updated.', closeDialog, closeDialog,'Ok'); fetchPartnerships(); }, onError: (e) => openDialog('Error', e?.response?.data?.error || e.message, closeDialog, closeDialog,'Ok') });
  const { mutate: archiveCampaign }         = useMutation(client.campaigns.archive,   { onSuccess: () => { openDialog('Success','Campaign archived.',   closeDialog, closeDialog,'Ok'); fetchCampaigns(); },    onError: (e) => openDialog('Error', e?.response?.data?.error || e.message, closeDialog, closeDialog,'Ok') });
  const { mutate: copyCampaign }            = useMutation(client.campaigns.copy,      { onSuccess: (data) => { fetchCampaigns(); if (data?.new_campaign_id) { navigate(`/editcampaign/${data.new_campaign_id}`); return; } openDialog('Success', data.message, closeDialog, closeDialog,'Ok'); }, onError: (e) => openDialog('Error', e?.response?.data?.error || e.message, closeDialog, closeDialog,'Ok') });
  const { mutate: postTracker }             = useMutation({ mutationFn: client.trackers.create, onSuccess: () => { fetchTrackers(); setOpenNewTracker(false); }, onError: (e) => alert(`Error: ${e?.response?.data?.error || e.message}`) });

  // ── Tracker delete mutation ───────────────────────────────────────────────────
  const { mutate: deleteTrackerMutation } = useMutation(
    (trackerId) => client.trackers.delete(trackerId),
    {
      onSuccess: () => {
        openDialog('Success', 'Tracker deleted.', closeDialog, closeDialog, 'Ok');
        fetchTrackers();
        fetchSharedTrackers();
      },
      onError: (e) => openDialog('Error', e?.response?.data?.error || e.message, closeDialog, closeDialog, 'Ok'),
    }
  );
  // ─────────────────────────────────────────────────────────────────────────────

  const handleItemClick = useCallback((item) => {
    if (item.type === 'creator_deal' && item._link) { navigate(item._link); return; }
    if (item.type === 'campaign')          navigate(`/editcampaign/${item.id}`);
    else if (item.type === 'partnership')  navigate(`/partnerships/${item.id}`);
    else if (item.type === 'casting_call') navigate(`/castingcall/${item.id}/manage`);
    else if (item.type === 'tracker')      { setSelectedTracker(item); setOpenTrackerDetail(true); }
  }, [navigate]);

  const toggleSelection = useCallback((id) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]), []);

  const handleShareCampaign = useCallback((id) => {
    navigator.clipboard.writeText(`${window.location.origin}/campaigns/${id}`);
    openDialog('Info', 'Share link copied!', closeDialog, closeDialog, 'Ok');
  }, [closeDialog, openDialog]);

  // ── handleDelete handles campaigns, partnerships, and trackers ───────────────
  const handleDelete = useCallback((item) => {
    if (item.type === 'tracker') {
      openDialog(
        'Confirm',
        <>Permanently delete tracker "{item.name}"? This cannot be undone.</>,
        () => { closeDialog(); deleteTrackerMutation(item.id); },
        closeDialog,
        'Delete',
        'Cancel'
      );
      return;
    }
    openDialog('Confirm', <>Delete "{item.name}"?</>, () => {
      if (item.type === 'campaign') deleteCampaign({ id: [item.id] });
      else                          updatePartnershipStatus({ id: item.id, status: 'DELETED' });
    }, closeDialog, 'Delete', 'Cancel');
  }, [deleteCampaign, deleteTrackerMutation, openDialog, closeDialog, updatePartnershipStatus]);
  // ─────────────────────────────────────────────────────────────────────────────

  const handleArchiveSelected = useCallback(() => {
    if (!selectedIds.length) return;
    selectedIds.forEach((id) => {
      const [type, itemId] = id.split('-');
      if (type === 'campaign') archiveCampaign({ type: 'campaign', id: itemId });
      else updatePartnershipStatus({ id: itemId, status: 'Archived' });
    });
    setSelectedIds([]);
  }, [selectedIds, archiveCampaign, updatePartnershipStatus]);

  const loading =
    isLoadingCampaigns ||
    isLoadingPartnerships ||
    isLoadingCastingCalls ||
    isLoadingTrackers ||
    isLoadingAgencyCollabs;

  const TABS = ['All','Campaigns','Partnerships','Casting Calls','Trackers', ...(incomingCount > 0 ? ['Incoming'] : []), 'Archived'];

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex' }}>
        <Box sx={{ flex: 1, px: { xs: 2, md: 4 }, pt: 3 }}>
          <Skeleton variant="text" width={220} height={44} />
          <Skeleton variant="text" width={320} height={26} sx={{ mt: 1 }} />
          <Stack direction="row" spacing={2} sx={{ mt: 2.5, mb: 4 }}>
            {[1,2,3].map((i) => <Skeleton key={i} variant="rounded" width={150} height={46} />)}
          </Stack>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {[1,2,3,4].map((i) => <Grid item xs={6} sm={3} key={i}><Skeleton variant="rounded" height={108} /></Grid>)}
          </Grid>
          <Grid container spacing={3}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}><Skeleton variant="rounded" height={CARD_HEIGHT} /></Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  const mainContent = (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', mb: { xs: 2, md: 3 }, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, letterSpacing: '-0.02em', mb: 0.25 }}>Collaborations</Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>{isMobile ? 'Manage your deals' : 'Campaigns, partnerships, casting calls & trackers'}</Typography>
        </Box>
        {!isMobile && (
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Button variant="contained" startIcon={<CampaignIcon />} onClick={() => navigate('/search')} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1.25 }}>Create Project</Button>
            {isAgency && <Button variant="outlined" startIcon={<PeopleIcon />} onClick={() => navigate('/agency')} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1.25 }}>My Agency</Button>}
            <Button variant="outlined" startIcon={<TrackerIcon />} onClick={() => setOpenNewTracker(true)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1.25, borderColor: 'info.main', color: 'info.main' }}>New Tracker</Button>
          </Stack>
        )}
      </Box>

      {isMobile && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Button variant="contained" size="small" startIcon={<CampaignIcon />} onClick={() => navigate('/search')} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}>Campaign</Button>
          {isAgency && <Button variant="outlined" size="small" startIcon={<PeopleIcon />} onClick={() => navigate('/agency')} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}>Agency</Button>}
          <Button variant="outlined" size="small" startIcon={<TrackerIcon />} onClick={() => setOpenNewTracker(true)} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', borderColor: 'info.main', color: 'info.main' }}>Tracker</Button>
        </Stack>
      )}

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={isAgency ? 3 : 4}><SummaryCard icon={<ActiveIcon />} label="Active" value={activeCount} sublabel="live now" color="success" onClick={() => { setTabValue('All'); setSearchStatus('active'); }} /></Grid>
        <Grid item xs={6} sm={isAgency ? 3 : 4}><SummaryCard icon={<PendingIconMui />} label="Needs Attention" value={withUnread} sublabel="new notifications" color="warning" /></Grid>
        {isAgency && incomingCount > 0 && <Grid item xs={6} sm={3}><SummaryCard icon={<InboxIcon />} label="Incoming Deals" value={incomingCount} sublabel="creator roster" color="info" onClick={() => setTabValue('Incoming')} /></Grid>}
        {isAgency && balance > 0 ? (
          <Grid item xs={6} sm={isAgency && incomingCount > 0 ? 3 : 4}><SummaryCard icon={<WalletIcon />} label="Balance" value={fmtMoney(balance)} sublabel="available in BlitzPay" color="success" onClick={() => setBalanceLedgerOpen(true)} /></Grid>
        ) : (
          <Grid item xs={6} sm={4}><SummaryCard icon={<ArchiveIconOutlined />} label="In Draft" value={draftCount} sublabel="waiting to launch" color="info" onClick={() => { setTabValue('All'); setSearchStatus('pending'); }} /></Grid>
        )}
      </Grid>

      {/* Deal mode toggle */}
      {isAgency && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.65rem' }}>Show:</Typography>
          <ToggleButtonGroup value={dealMode} exclusive onChange={(_, v) => v && setDealMode(v)} size="small" sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', px: 1.5, py: 0.5, borderRadius: '8px !important', border: `1px solid ${theme.palette.divider} !important`, mx: '2px !important' } }}>
            <ToggleButton value="all"><AllIcon sx={{ fontSize: 14, mr: 0.5 }} />All</ToggleButton>
            <ToggleButton value="outbound"><SendIcon sx={{ fontSize: 14, mr: 0.5 }} />My Campaigns</ToggleButton>
            <ToggleButton value="inbound"><InboxIcon sx={{ fontSize: 14, mr: 0.5 }} />Creator Deals</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
            {dealMode === 'inbound' ? 'Showing deals FOR your creators' : dealMode === 'outbound' ? 'Showing your outbound campaigns' : 'Showing everything'}
          </Typography>
        </Box>
      )}

      {/* Search / filter / tabs */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper', mb: 3 }}>
        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', borderBottom: `1px solid ${theme.palette.divider}` }}>
          <TextField
            variant="outlined" size="small" placeholder="Search by name…"
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: '1 1 460px',
              maxWidth: { xs: '100%', md: 780 },
              '& .MuiOutlinedInput-root': { borderRadius: 2, height: 38 },
            }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Status</InputLabel>
            <Select value={searchStatus} onChange={(e) => setSearchStatus(e.target.value)} label="Status" sx={{ borderRadius: 2, height: 38 }}>
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="active">Active / Launched</MenuItem>
              <MenuItem value="pending">Pending / Draft</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
          {searchStatus && (
            <Button size="small" variant="text" onClick={() => setSearchStatus('')} sx={{ textTransform: 'none', fontWeight: 600, height: 38, px: 1 }}>
              Clear
            </Button>
          )}
          <ButtonGroup size="small" variant="outlined" sx={{ height: 38 }}>
            <Button onClick={() => setIsPrettyView(true)}  variant={isPrettyView  ? 'contained' : 'outlined'} color={isPrettyView  ? 'primary' : 'inherit'}><GridViewIcon  fontSize="small" /></Button>
            <Button onClick={() => setIsPrettyView(false)} variant={!isPrettyView ? 'contained' : 'outlined'} color={!isPrettyView ? 'primary' : 'inherit'}><TableRowsIcon fontSize="small" /></Button>
          </ButtonGroup>
          {selectedIds.length > 0 && (
            <Button variant="contained" size="small" startIcon={<ArchiveIcon />} onClick={handleArchiveSelected} color="warning" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, height: 38, whiteSpace: 'nowrap' }}>Archive ({selectedIds.length})</Button>
          )}
        </Box>

        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto" sx={{ px: 1, '& .MuiTabs-indicator': { height: 3, borderRadius: 2 }, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.83rem', minHeight: 46, px: 2 } }}>
          {TABS.map((t) => (
            <Tab key={t} value={t} label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t === 'Trackers' && <TrackerIcon sx={{ fontSize: 14 }} />}
                {t === 'Incoming' && <InboxIcon   sx={{ fontSize: 14 }} />}
                {t}
                {tabCounts[t] !== undefined && tabCounts[t] > 0 && (
                  <Chip
                    size="small"
                    label={tabCounts[t]}
                    sx={{
                      height: 17,
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      bgcolor: t === tabValue ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.13),
                      color: t === tabValue ? '#fff' : theme.palette.text.primary,
                    }}
                  />
                )}
              </Box>
            } />
          ))}
        </Tabs>
      </Card>

      {/* Content */}
      {filteredCollaborations.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <HandshakeIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600}>No collaborations found</Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>{searchQuery || searchStatus ? 'Try adjusting your filters' : 'Create your first campaign to get started'}</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/search')} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>New Campaign</Button>
        </Box>
      ) : isPrettyView ? (
        <Grid container spacing={3}>
          {paginatedCollaborations.map((item) => (
            <Grid item xs={12} sm={6} md={isDesktop && isAgency && !sidebarMinimized ? 6 : 4} lg={isDesktop && isAgency && !sidebarMinimized ? 4 : 3} key={`${item.type}-${item.id}`}>
              <CollabCard item={item} isSelected={selectedIds.includes(`${item.type}-${item.id}`)} onToggleSelect={toggleSelection} onOpen={handleItemClick} onDelete={handleDelete} onShare={handleShareCampaign} onCopy={(id) => copyCampaign({ campaignId: id })} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIds.length > 0 && selectedIds.length < paginatedCollaborations.length}
                      checked={paginatedCollaborations.length > 0 && paginatedCollaborations.every((i) => selectedIds.includes(`${i.type}-${i.id}`))}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedIds((prev) => [...new Set([...prev, ...paginatedCollaborations.map((i) => `${i.type}-${i.id}`)])]);
                        else { const ids = paginatedCollaborations.map((i) => `${i.type}-${i.id}`); setSelectedIds((prev) => prev.filter((id) => !ids.includes(id))); }
                      }}
                    />
                  </TableCell>
                  {['Name','Type','Status','Details',''].map((h) => <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary', py: 1.5 }}>{h}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCollaborations.map((item) => {
                  const itemId     = `${item.type}-${item.id}`;
                  const isSelected = selectedIds.includes(itemId);
                  const isCampaign = item.type === 'campaign';
                  const isTracker  = item.type === 'tracker';
                  const isIncoming = item.type === 'creator_deal';
                  const status     = isCampaign ? item.campaign_status : item.status;
                  const meta       = TYPE_META[item.type] || { label: item.type, color: 'default' };
                  return (
                    <TableRow key={itemId} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) }, ...(isSelected && { bgcolor: alpha(theme.palette.primary.main, 0.06) }) }} onClick={() => handleItemClick(item)}>
                      <TableCell padding="checkbox" onClick={(e) => { e.stopPropagation(); toggleSelection(itemId); }}><Checkbox checked={isSelected} sx={{ pointerEvents: 'none' }} /></TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Badge color="error" badgeContent={item.unread_notifs} invisible={!item.unread_notifs} overlap="circular">
                            <Avatar src={item.type === 'tracker' ? item.tracker_photo : (isIncoming ? item.creator_pfphref : item.pfphref)} sx={{ width: 34, height: 34, bgcolor: `${meta.color}.light` }}>
                              {item.type === 'tracker' && <TrackerIcon sx={{ fontSize: 17 }} />}
                            </Avatar>
                          </Badge>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 200 }}>{item.name}</Typography>
                          {/* ── v8: internal duplicate flag in table view ── */}
                          {item._internalDealLink && (
                            <Tooltip title="See internal incoming deal">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleItemClick({ type: 'creator_deal', _link: item._internalDealLink }); }} sx={{ color: 'info.main' }}>
                                <InboxIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell><Chip label={meta.label} color={meta.color} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                      <TableCell><Chip label={String(status || 'N/A').charAt(0).toUpperCase() + String(status || 'N/A').slice(1)} color={STATUS_COLOR[status] || 'default'} size="small" sx={{ fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{isCampaign ? (item.campaign_sum ? fmtSum(item.campaign_sum) : item.user?.username) : item.type === 'tracker' ? `${item.links?.length || 0} links` : item.type === 'casting_call' ? (item.user_role === 'owner' ? 'Managing' : 'Collab') : isIncoming ? (item.creator_name || '—') : (item.email || '—')}</Typography></TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {isCampaign && (<><Tooltip title="Share"><IconButton size="small" onClick={() => handleShareCampaign(item.id)} sx={{ color: 'info.main' }}><ShareIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip><Tooltip title="Duplicate"><IconButton size="small" onClick={() => copyCampaign({ campaignId: item.id })} sx={{ color: 'secondary.main' }}><ContentCopyIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip></>)}
                          {/* ── DELETE in table view: campaigns, partnerships, AND trackers ── */}
                          {(isCampaign || item.type === 'partnership' || isTracker) && (
                            <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(item)} sx={{ color: 'error.main' }}><DeleteIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                          )}
                          <Tooltip title="Open"><IconButton size="small" onClick={() => handleItemClick(item)} sx={{ color: 'primary.main' }}><OpenInNewIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {filteredCollaborations.length > itemsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination count={Math.ceil(filteredCollaborations.length / itemsPerPage)} page={page + 1} onChange={(_, v) => setPage(v - 1)} shape="rounded" color="primary" showFirstButton showLastButton />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <AlertDialog alertState={dialogState} />
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        {!isMobile && isAgency ? (
          <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
            <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', px: 4, py: 3 }}>{mainContent}</Box>
            <Box sx={{ width: sidebarMinimized ? SIDEBAR_MINI : SIDEBAR_FULL, flexShrink: 0, transition: 'width 0.25s ease', height: '100vh', position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
              <AgencyCreatorSidebar minimized={sidebarMinimized} onToggle={() => setSidebarMinimized((v) => !v)} />
            </Box>
          </Box>
        ) : (
          <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 2, md: 3 } }}>{mainContent}</Box>
        )}
      </Box>
      <NewTracker open={openNewTracker} handleAddTracker={(t) => postTracker(t)} handleClose={() => setOpenNewTracker(false)} />
      <Tracker open={openTrackerDetail} handleClose={() => { setOpenTrackerDetail(false); fetchTrackers(); fetchSharedTrackers(); }} tracker={selectedTracker} isOwner={!selectedTracker?.is_shared} />
      <CollabsBalanceLedger open={balanceLedgerOpen} onClose={() => setBalanceLedgerOpen(false)} />
    </>
  );
};

export default Collaborations;
